// Auth store (zustand). Holds the logged-in user and a coarse session status.
// - checkSession(): GET /api/me on app load → "authed" | "anon"
// - login(user, pass): POST /api/login → on success refresh /api/me
// - logout(): POST /api/logout → clears local state
//
// The server owns the real session (HttpOnly cookie); this store mirrors it so the
// router can gate routes without re-fetching on every render.

import { create } from "zustand";
import { api, isUnauthorized } from "./api";

export type SessionStatus = "idle" | "checking" | "authed" | "anon";

interface AuthState {
  user: string | null;
  status: SessionStatus;
  /** Last login error message, shown on the login form. */
  error: string | null;
  loggingIn: boolean;

  checkSession: () => Promise<void>;
  login: (user: string, pass: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: "idle",
  error: null,
  loggingIn: false,

  checkSession: async () => {
    set({ status: "checking" });
    try {
      const { user } = await api.me();
      set({ user, status: "authed" });
    } catch {
      set({ user: null, status: "anon" });
    }
  },

  login: async (user, pass) => {
    set({ loggingIn: true, error: null });
    try {
      await api.login(user, pass);
      // Confirm the session is live and capture the canonical username.
      const me = await api.me();
      set({ user: me.user, status: "authed", loggingIn: false });
      return true;
    } catch (e) {
      const msg = isUnauthorized(e)
        ? "Invalid username or password"
        : e instanceof Error
          ? e.message
          : "Login failed";
      set({ error: msg, loggingIn: false, status: "anon" });
      return false;
    }
  },

  logout: async () => {
    try {
      await api.logout();
    } catch {
      // Even if the network call fails, drop local auth so the UI locks.
    }
    set({ user: null, status: "anon", error: null });
  },

  clearError: () => set({ error: null }),
}));
